import { ErrorType } from "@/shared/config/error-type";
import { ContentType, ContentTypeType, HttpResponse } from "@/shared/config/globals";
import { HttpClient, Timeout, TimeoutType } from "@/shared/services/http-client";
import { AxiosHeaders, AxiosRequestHeaders, ResponseType } from "axios";
import { EndpointPathNames } from "@/shared/config/endpoint-path";

// 名前付き引数用の関数共通パラメータ（GET、DELETE用）
type GetOrDeleteRequestArgs = {
  path: string;
  params?: { [key: string]: string | number | boolean };
  timeout?: TimeoutType;
  responseType?: ResponseType;
};

// 名前付き引数用の関数共通パラメータ（POST、PUT用）
type PostOrPutRequestArgs = {
  path: string;
  params?: { [key: string]: string | number | boolean };
  timeout?: TimeoutType;
  contentType?: ContentTypeType;
  responseType?: ResponseType;
};

/**
 * Api通信共通部品クラス
 */
export class ApiClient {
  private static _instance: ApiClient;
  private constructor() {  /**intentionally empty */ }

  static get instance(): ApiClient {
    if (this._instance === null || this._instance === undefined) {
      this._instance = new ApiClient();
    }
    return this._instance;
  }

  /**
   * GETメソッドでのHTTP通信を実行する
   *
   * @param {GetOrDeleteRequestArgs}
   *   path         // パス
   *   params       // クエリパラメータ
   *   timeout      // タイムアウト
   *   responseType // レスポンスタイプ
   * @returns {*}  {Promise<HttpResponse<T>>}
   */
  public async get<T>({
    path,
    params = {},
    timeout = Timeout.Long,
    responseType = "json",
  }: GetOrDeleteRequestArgs): Promise<HttpResponse<T>> {

    // HttpClientの初期化
    const httpClient = new HttpClient();

    // メソッド実行
    const res = await this.executeApiRequest<T>(
      (headers) => { return httpClient.get<T>(path, headers, params, timeout, responseType); }
    );

    return res;
  }

  /**
   * GETメソッドでのHTTP通信を実行する（Excelデータ）
   *
   * @param {GetOrDeleteRequestArgs}
   *   path         // パス
   *   params       // クエリパラメータ
   *   timeout      // タイムアウト
   *   responseType // レスポンスタイプ
   * @returns {*}  {Promise<HttpResponse<T>>}
   */
  public async getBlob<T>({
    path,
    params = {},
    timeout = Timeout.Long,
    responseType = "blob",
  }: GetOrDeleteRequestArgs): Promise<HttpResponse<T>> {

    // HttpClientの初期化
    const httpClient = new HttpClient();
    // メソッド実行
    const res = await this.executeApiRequest<T>(
      (headers) => { return httpClient.getBlob<T>(path, headers, params, timeout, responseType); }
    );

    return res;
  }

  /**
   * POSTメソッドでのHTTP通信を実行する
   *
   * @param {PostOrPutRequestArgs}
   *   path         // パス
   *   params       // クエリパラメータ
   *   timeout      // タイムアウト
   *   contentType  // コンテントタイプ
   *   responseType // レスポンスタイプ
   * @returns {*}  {Promise<HttpResponse<T>>}
   */
  public async post<T>({
    path,
    params = {},
    timeout = Timeout.Long,
    contentType = ContentType.applicationJson,
    responseType = "json",
  }: PostOrPutRequestArgs): Promise<HttpResponse<T>> {

    // HttpClientの初期化
    const httpClient = new HttpClient();

    // メソッド実行
    const res = await this.executeApiRequest<T>(
      (headers) => { return httpClient.post<T>(path, headers, params, timeout, responseType); },
      contentType
    );

    return res;
  }

  /**
   * POSTメソッドでのHTTP通信を実行する（Excelデータ）
   *
   * @param {PostBytesOrPutRequestArgs}
   *   path         // パス
   *   params       // クエリパラメータ
   *   timeout      // タイムアウト
   *   contentType  // コンテントタイプ
   *   responseType // レスポンスタイプ
   * @returns {*}  {Promise<HttpResponse<T>>}
   */
  public async postBlob<T>({
    path,
    params = {},
    timeout = Timeout.Long,
    contentType = ContentType.applicationJson,
    responseType = "blob",
  }: PostOrPutRequestArgs): Promise<HttpResponse<T>> {

    // HttpClientの初期化
    const httpClient = new HttpClient();
    // メソッド実行
    const res = await this.executeApiRequest<T>(
      (headers) => { return httpClient.postBlob<T>(path, headers, params, timeout, responseType); },
      contentType
    );

    return res;
  }

  /**
   * POSTメソッドでのHTTP通信を実行する（画像データ）
   *
   * @param {PostBytesOrPutRequestArgs}
   *   path         // パス
   *   params       // クエリパラメータ
   *   timeout      // タイムアウト
   *   contentType  // コンテントタイプ
   *   responseType // レスポンスタイプ
   * @returns {*}  {Promise<HttpResponse<T>>}
   */
  public async postBytes<T>({
    path,
    params = {},
    timeout = Timeout.Long,
    contentType = ContentType.formData,
    responseType = "json",
  }: PostOrPutRequestArgs): Promise<HttpResponse<T>> {

    // HttpClientの初期化
    const httpClient = new HttpClient();
    // メソッド実行
    const res = await this.executeApiRequest<T>(
      (headers) => { return httpClient.postBytes<T>(path, headers, params, timeout, responseType); },
      contentType
    );

    return res;
  }

  /**
   * PUTメソッドでのHTTP通信を実行する
   *
   * @param {PostOrPutRequestArgs}
   *   path         // パス
   *   params       // クエリパラメータ
   *   timeout      // タイムアウト
   *   contentType  // コンテントタイプ
   *   responseType // レスポンスタイプ
   * @returns {*}  {Promise<HttpResponse<T>>}
   */
  public async put<T>({
    path,
    params = {},
    timeout = Timeout.Long,
    contentType = ContentType.applicationJson,
    responseType = "json",
  }: PostOrPutRequestArgs): Promise<HttpResponse<T>> {

    // HttpClientの初期化
    const httpClient = new HttpClient();

    // メソッド実行
    const res = await this.executeApiRequest<T>(
      (headers) => { return httpClient.put<T>(path, headers, params, timeout, responseType); },
      contentType
    );

    return res;
  }

  /**
   * DELETEメソッドでのHTTP通信を実行する
   *
   * @param {GetOrDeleteRequestArgs}
   *   path         // パス
   *   params       // クエリパラメータ
   *   timeout      // タイムアウト
   *   responseType // レスポンスタイプ
   * @returns {*}  {Promise<HttpResponse<T>>}
   */
  public async delete<T>({
    path,
    params = {},
    timeout = Timeout.Long,
    responseType = "json",
  }: GetOrDeleteRequestArgs): Promise<HttpResponse<T>> {

    // HttpClientの初期化
    const httpClient = new HttpClient();

    // メソッド実行
    const res = await this.executeApiRequest<T>(
      (headers) => { return httpClient.delete<T>(path, headers, params, timeout, responseType); }
    );

    return res;
  }

  /**
   * HTTPリクエストを実行する
   *
   * @param {((headers: AxiosRequestHeaders) => Promise<HttpResponse<T>>)} func
   * @param {string} [contentType]
   * @returns {*}  {(Promise<HttpResponse<T>>)}
   */
  private async executeApiRequest<T>(
    /// 実行するHTTPリクエスト
    func: (headers: AxiosRequestHeaders) => Promise<HttpResponse<T>>,
    contentType?: ContentTypeType // コンテントタイプ
  ): Promise<HttpResponse<T>> {
    // TODO: 実行時のログ出力

    // ヘッダー作成
    const headers = this.createHeaders(contentType);

    // リクエスト実行
    const result = await func(headers);

    // 失敗時はエラー種別に応じて処理
    // TODO: エラー洗い出し
    const errorType = (result as HttpResponse<T>).errorType;

    //////////////////////////////////////////////////////////////////////////
    // セッション無効を検知
    if (errorType === ErrorType.MAINTENANCE_ERROR) {
      // ブラウザバックで戻れないよう履歴を削除する
      window.location.replace(EndpointPathNames.LOGIN + "?maintenance=1");
    }
    if (errorType === ErrorType.SESSION_INVALID) {
      // ブラウザバックで戻れないよう履歴を削除する
      window.location.replace(EndpointPathNames.LOGIN + "?session_invalid=true");
    }

    // 上記以外はそのまま返却する
    return result;
  }

  /**
   * ヘッダを作成する
   *
   * @private
   * @param {string} [ContentTypeType]
   * @returns {*}  {AxiosRequestHeaders}
   * @memberof ApiClient
   */
  private createHeaders(
    contentType?: ContentTypeType
  ): AxiosRequestHeaders {
    const header = new AxiosHeaders();

    if (contentType !== null && contentType !== undefined) {
      header.set("Content-Type", contentType.valueOf());
    }

    // フロントとバックが同一ドメインの場合withCredentialsは設定しなくてもセッションは送信される

    return header;
  }
}
